import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Col, Form, Row, Steps, Typography, Button, message, Spin, Alert, Space
} from 'antd';
import {
    AimOutlined,
    FileTextOutlined, RocketOutlined, ToolOutlined,
    ClockCircleOutlined, AppstoreAddOutlined
} from '@ant-design/icons';


import * as lessonPlanApi from './services/lessonPlanApi';
import InteractiveLessonPlan from './InteractiveLessonPlan';

import LearningObjective from './wizardSteps/LearningObjective';
import ResourcesCustomization from './wizardSteps/ResourcesCustomization';
import StructureComponents from './wizardSteps/StructureComponents';

const { Title, Paragraph } = Typography;
const POLLING_INTERVAL = 3000; // Poll every 3 seconds

const LessonPlanWizard = ({
                              wizardConfig,
                              curriculumData,
                              instructionalBlocks,
                              onClose
                          }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [form] = Form.useForm();
    const watchedValues = Form.useWatch([], form);
    const [formValues, setFormValues] = useState({});

    // --- State for Wizard Logic & API Responses (Internal to wizard) ---
    const [isGenerating, setIsGenerating] = useState(false);
    const [isValidating, setIsValidating] = useState(false);

    // --- NEW: State to manage button disabling ---
    const [isStepValid, setIsStepValid] = useState(false);

    const [pollingUuid, setPollingUuid] = useState(null);
    const [finalLessonPlan, setFinalLessonPlan] = useState(null);
    const [generationConfig, setGenerationConfig] = useState(null);

    // --- State for dynamic templates ---
    const [suggestedTemplates, setSuggestedTemplates] = useState([]);
    const [isTemplateLoading, setIsTemplateLoading] = useState(false);

    // --- State for dependent dropdowns ---
    const [topicSource, setTopicSource] = useState('TEXTBOOK');
    const [isOnlineMode, setIsOnlineMode] = useState(false);

    // --- Reusable function to set form defaults ---
    const initializeFormDefaults = useCallback((config) => {
        if (!config) return;

        let allResources = [];
        if (config.contentTypes) {
            const { instructional = [], practice = [], assessment = [] } = config.contentTypes;
            allResources = [
                ...instructional.map(item => item.value),
                ...practice.map(item => item.value),
                ...assessment.map(item => item.value),
            ];
        }
        const allLessonPlanComponents = config.lessonPlanComponents?.map(item => item.value) || [];

        const initialForm = {
            subject: 'MATHEMATICS',
            learningGoal: 'INTRODUCE_NEW_TOPIC',
            knowledgeDimension: 'FACTUAL',
            timeframe: 'SINGLE_LESSON_SEGMENTED',
            dokLevel: 'LEVEL_1',
            topicSource: 'TEXTBOOK',
            selectedResources: allResources,
            classroomFormat: 'IN_CLASS_TEACHER_LED',
            resourceProfile: 'LOW_TECH',
            assessmentFitCategory: 'QUIZ_EXIT_TICKET',
            wellBeingFocus: 'CONFIDENCE',
            lessonPlanComponents: allLessonPlanComponents,
        };

        const { lessonPlanTemplateId, ...defaultsToSet } = initialForm;

        form.setFieldsValue(defaultsToSet);
        setFormValues(defaultsToSet); // This will trigger the validation effect

        // Set dependent state
        setTopicSource(initialForm.topicSource);
        setIsOnlineMode(initialForm.classroomFormat === 'ONLINE_SYNCHRONOUS');

    }, [form]);

    // --- Initialize form when config prop is ready ---
    useEffect(() => {
        if (wizardConfig) {
            initializeFormDefaults(wizardConfig);
        }
    }, [wizardConfig, initializeFormDefaults]);


    useEffect(() => {
        const validateCurrentStep = () => {
            const {
                learningGoal, knowledgeDimension, dokLevel, topicSource,
                conceptId, acaraCode, selectedResources, classroomFormat,
                resourceProfile, assessmentFitCategory, wellBeingFocus,
                lessonPlanTemplateId, lessonPlanComponents
            } = watchedValues || {};

            if (currentStep === 0) {
                const hasBaseFields = learningGoal && knowledgeDimension && dokLevel && topicSource;
                if (!hasBaseFields) return false;
                if (topicSource === 'TEXTBOOK' && !conceptId) return false;
                if (topicSource === 'ACARA_CODE' && !acaraCode) return false;
                return true;
            }

            if (currentStep === 1) {
                const hasBaseFields = classroomFormat && resourceProfile && assessmentFitCategory && wellBeingFocus;
                if (!hasBaseFields) return false;
                if (!selectedResources || selectedResources.length === 0) return false;
                return true;
            }

            if (currentStep === 2) {
                if (!lessonPlanTemplateId) return false;
                if (!lessonPlanComponents || lessonPlanComponents.length === 0) return false;
                return true;
            }
            return true;
        };

        setIsStepValid(validateCurrentStep());
    }, [currentStep, watchedValues]);



    useEffect(() => {
        if (!pollingUuid) return;

        const poller = setInterval(async () => {
            try {
                const result = await lessonPlanApi.getGenerationStatus(pollingUuid);
                if (result.status === 'PENDING') {
                    return;
                }
                if (result.status === 'COMPLETE') {
                    setPollingUuid(null);
                    setIsGenerating(false);
                    setFinalLessonPlan(result.lessonPlanContent);

                    message.success('Lesson plan generated successfully!');
                    setCurrentStep(4);
                } else if (result.status === 'FAILED') {
                    setPollingUuid(null);
                    setIsGenerating(false);
                    const errorMessage = result.error?.message || 'Lesson plan generation failed.';
                    message.error(errorMessage);
                    setCurrentStep(2); // MODIFIED: Go back to last step (now step 2)
                }
            } catch (err) {
                setPollingUuid(null);
                setIsGenerating(false);
                message.error('An error occurred while checking the lesson status.');
                setCurrentStep(2); // MODIFIED: Go back to last step (now step 2)
            }
        }, POLLING_INTERVAL);

        return () => clearInterval(poller);
    }, [pollingUuid]);

    // --- Template Fetching Effect (MODIFIED for new step index) ---
    useEffect(() => {
        const fetchTemplates = async () => {
            // MODIFIED: Step 2 is now 'Structure & Components'
            if (currentStep === 2) {
                setIsTemplateLoading(true);
                try {
                    const currentFormValues = form.getFieldsValue(true);
                    const templates = await lessonPlanApi.searchTemplates(currentFormValues);
                    setSuggestedTemplates(templates || []);

                    if (templates && templates.length > 0) {
                        const firstTemplateId = templates[0].id;
                        form.setFieldsValue({ lessonPlanTemplateId: firstTemplateId });
                        // Manually update formValues state to trigger validation
                        setFormValues(prev => ({ ...prev, lessonPlanTemplateId: firstTemplateId }));
                    }

                } catch (err) {
                    message.error(err.message || 'Failed to load suggested templates.');
                } finally {
                    setIsTemplateLoading(false);
                }
            }
        };
        fetchTemplates();
    }, [currentStep, form]); // Removed setFormValues from dependency array


    // --- Handlers ---
    const handleFormChange = (_, allValues) => {
        setFormValues(allValues);

        if (allValues.classroomFormat) setIsOnlineMode(allValues.classroomFormat === 'ONLINE_SYNCHRONOUS');
        if (allValues.topicSource) setTopicSource(allValues.topicSource);
    };

    const handleNext = async () => {
        // We still run validateFields() for AntD's visual error messages
        try {
            await form.validateFields();

            // 2. If on the first step (Learning Objective), run special API validation
            if (currentStep === 0) {
                setIsValidating(true);
                const configPayload = form.getFieldsValue(true);

                try {
                    // 3. Call the new validation API
                    await lessonPlanApi.validateTeachingSkill(configPayload);
                    // 4. If successful, proceed to the next step
                    setCurrentStep(s => s + 1);
                } catch (err) {
                    // 5. If API fails, show the error message from the server
                    message.error(err.message || 'Failed to validate teaching skill.');
                } finally {
                    setIsValidating(false);
                }
            } else {
                // 6. For all other steps, just go to the next step
                setCurrentStep(s => s + 1);
            }
        } catch (validationError) {
            // This catch block handles Ant Design's form.validateFields() failures
            console.log('Form validation failed:', validationError);
            // Don't proceed if form fields are invalid
        }
    };

    const handlePrev = () => setCurrentStep(s => s - 1);

    const onInitialSubmit = async (values) => {
        setIsGenerating(true);
        const configPayload = form.getFieldsValue(true);
        setGenerationConfig(null); // Clear previous config

        try {
            const response = await lessonPlanApi.startLessonPlanGeneration(configPayload);
            setPollingUuid(response.lessonPlanUuid);
            setGenerationConfig(response);
            setCurrentStep(3);
        } catch (err) {
            message.error(err.message || 'Failed to start lesson plan generation.');
            setIsGenerating(false);
        }
    };

    // --- Loading and Error States ---
    if (!wizardConfig || !curriculumData) {
        return <Spin description="Loading wizard configuration..." size="large" style={{ display: 'block', margin: '50px auto' }} />;
    }

    // --- MODIFIED: Step Definitions ---
    const steps = [
        // ... (steps content is unchanged) ...
        {
            title: 'Learning Objective',
            icon: <AimOutlined />, // Used Goal icon
            content: <LearningObjective
                topicSource={topicSource}
                options={wizardConfig}
            />
        },
        {
            title: 'Resources & Focus',
            icon: <ToolOutlined />,
            content: <ResourcesCustomization
                contentTypes={wizardConfig?.contentTypes}
                options={wizardConfig}
                isOnlineMode={isOnlineMode}
            />
        },
        {
            title: 'Structure & Components',
            icon: <AppstoreAddOutlined />,
            content: <StructureComponents
                form={form}
                formValues={formValues}
                setFormValues={setFormValues}
                suggestedTemplates={suggestedTemplates}
                allTemplates={wizardConfig?.lessonTemplates || []}
                allComponents={wizardConfig?.lessonPlanComponents || []}
                isLoading={isTemplateLoading}
            />
        },
        {
            title: 'Generating',
            icon: <ClockCircleOutlined />,
            content: (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Spin size="large" description="Our AI is crafting your lesson plan... Please wait." />
                    <Alert
                        message="This can take up to a minute"
                        description="We'll automatically advance when it's ready."
                        type="info"
                        showIcon
                        style={{ marginTop: 24, textAlign: 'left' }}
                    />
                </div>
            )
        },
        {
            title: 'Preview',
            icon: <FileTextOutlined />,
            content: <InteractiveLessonPlan
                formValues={{ ...formValues, ...(generationConfig || {}) }}
                lessonContent={finalLessonPlan}
                instructionalBlocks={instructionalBlocks}
                contentTypes={wizardConfig?.contentTypes || {}}
            />
        },
    ];

    const currentStepConfig = steps[currentStep];

    return (
        <>
            <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
                <Col>
                    <Title level={2} style={{ margin: 0 }}>New Lesson Plan</Title>
                </Col>
            </Row>

            {currentStep < 3 &&
                <Steps current={currentStep} style={{ marginBottom: '24px' }}>
                    {steps.slice(0, 3).map((s, i) => <Steps.Step key={i} title={s.title} icon={s.icon} />)}
                </Steps>
            }
            {currentStep >= 3 &&
                <Alert
                    message={<Title level={4} style={{ margin: 0 }}>{currentStepConfig?.title}</Title>}
                    type="info"
                    style={{ marginBottom: '24px' }}
                    showIcon
                    icon={currentStepConfig?.icon}
                />
            }


            <Form form={form} layout="vertical" onFinish={onInitialSubmit} onValuesChange={handleFormChange}>
                <Card style={{ padding: '16px' }}>
                    {currentStepConfig?.content}
                </Card>

                <Row justify="space-between" style={{ marginTop: 24 }}>
                    <Col>
                        <Space>
                            {currentStep > 0 && currentStep < 3 &&
                                <Button onClick={handlePrev} size="large" disabled={isGenerating}>Previous</Button>
                            }
                            {currentStep < 3 &&
                                <Button onClick={onClose} size="large" disabled={isGenerating || isValidating}>Cancel</Button>
                            }
                        </Space>
                    </Col>
                    <Col>
                        {currentStep < 2 &&
                            <Button
                                type="primary"
                                onClick={handleNext}
                                size="large"
                                loading={isValidating}
                                disabled={!isStepValid || isValidating}
                            >
                                Next
                            </Button>
                        }
                        {currentStep === 2 &&
                            <Button
                                type="primary"
                                htmlType="submit"
                                size="large"
                                loading={isGenerating}
                                icon={<RocketOutlined />}
                                disabled={!isStepValid || isGenerating}
                            >
                                Generate Lesson Plan
                            </Button>
                        }
                        {currentStep === 4 && <Button type="primary" size="large" onClick={() => {
                            onClose();
                            setFinalLessonPlan(null);
                            setCurrentStep(0);
                            initializeFormDefaults(wizardConfig);
                        }}>Done (Go to Library)</Button>}
                    </Col>
                </Row>
            </Form>
        </>
    );
};

export default LessonPlanWizard;
